import { useLandingDiscoveryFeatures } from 'app/hooks/use-landing-discovery-features';
import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import type { MutationFunction, UndefinedInitialDataOptions, UseMutationOptions } from '@tanstack/react-query';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getBffQueryData, useInfiniteQueryLPC, useQueryLPC } from 'app/lib/query';
import { asFormData } from 'app/lib/form';
import type { ImagePickerAsset } from 'expo-image-picker';
import type { FunctionalUseInfiniteQueryOptions, FunctionalUseQueryOptions, Pagination } from 'app/types/network';
import type { User } from 'app/types/user';
import { useAtomValue, useSetAtom } from 'jotai';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { http } from 'app/lib/axios';
import type { Address } from 'app/types/address';
import { useAllAddresses } from './address';
import type { Group } from 'app/types/group';
import { useAllGroups } from './groups';
import type { Profile } from '../types/profile';
import { listingKeys, recommendationKeys } from './keys';
import { useGetQueryKeys, useQueryKeys } from './invalidation';
import { normalizeListingFilters } from './listing-query-filters';
import {
  bffDiscoverScreenLoadedAtom,
  bffUserProfileLoadedAtom,
  isLoggedInAtom,
  isStartingAtom,
  newListingSourceAtom,
  selfUserLoadedAtom,
  userInfoAtom,
} from 'app/atoms';
import { Event, track, trackOnFacebook, CustomFacebookEvent, ContributionType } from 'app/lib/analytics';
import { type Preference } from 'app/types/preferences';
import type { UpdatedSearchDistanceSignalData } from 'app/network/signals';
import { getSignalData, Signal } from 'app/network/signals';
import { useResetAtom } from 'jotai/utils';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import type {
  Favorite,
  FavouriteGrouping,
  Listing,
  ListingBorrower,
  ListingPicture,
  NewListingSource,
  ListingGrouping,
  ListingsSortBy,
  FindAllListingsDTO,
  CreateFavouriteDTO,
  CreateGroupingFavouriteDTO,
  CreateListingDTO,
  UpdateListingDTO,
  CreateListingsDTO,
  FindListingsMapDTO,
  ListingMapViewport,
  ListingsMapResponse,
} from 'app/types/listing';
import { ListingType, ListingSubtype } from 'app/types/listing';
import type { PublicLocation } from 'app/types/location';
import {
  canGuestsUseListingCategories,
  canUseListingCategories,
  canUseListingTags,
  isWhiteLabel,
} from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { useCanUseServices } from './category';
import type { FilterPreferencesForQuery } from 'app/types/listings-filters';
import { getAddressPayload } from './address-utils';
import type { CustomTabFilters } from 'app/schemas/user';
import { getI18nLanguage, type Language } from 'app/lib/language';
import { MAX_RADIUS_IN_METERS } from 'app/network/preferences';
import {
  hasListingUser,
  mergeListingDetailCache,
  mergeListingDetails,
  updateGroupingFavouriteCache,
} from './listing-cache';
import { getListingTransactionFields } from './listing-transaction-fields';
import { objectsAreEquivalent } from 'app/utils/constants';
export { ListingsSortBy } from 'app/types/listing';
export type { NewListingSource } from 'app/types/listing';

export function getAvailableListingTagIds(
  selected: string[] | undefined,
  available: Tag[] | undefined,
  featureFlags?: User['featureFlags'],
) {
  if (!canUseListingTags(featureFlags)) return undefined;
  return isWhiteLabel(WhiteLabel.Mutuo) && available
    ? selected?.filter((id) => available.some((tag) => tag.id === id))
    : selected;
}

export const getListingsFilters = (
  filtersPreference: FilterPreferencesForQuery,
  search: string,
  type: ListingType,
  isLoggedIn: boolean,
  forcedFilters?: CustomTabFilters,
  sortBy?: ListingsSortBy,
) => {
  const categoriesEnabled = isLoggedIn
    ? canUseListingCategories(filtersPreference.featureFlags)
    : canGuestsUseListingCategories();
  const groupFiltersCount = filtersPreference.availableValues.groups?.length;
  const selectedGroupsCount = filtersPreference.selectedGroups?.length;
  const groupIdsFilterActive = Boolean(
    groupFiltersCount &&
    selectedGroupsCount !== undefined &&
    /* selectedGroupsCount > 0 && -> for groups do not add this because we want to be able to show group only listings! */
    selectedGroupsCount < groupFiltersCount,
  );

  const subtypes = forcedFilters?.subtypes ?? filtersPreference.selectedSubtypes ?? [];
  const listsServicesOnly = subtypes.length === 1 && subtypes[0] === ListingSubtype.service;

  return {
    addressIds:
      !filtersPreference.availableValues.addresses ||
      !filtersPreference.selectedAddresses ||
      filtersPreference.selectedAddresses.length === filtersPreference.availableValues.addresses.length
        ? undefined
        : filtersPreference.selectedAddresses,
    groupIds:
      !filtersPreference.availableValues.groups ||
      !filtersPreference.selectedGroups ||
      filtersPreference.selectedGroups.length === filtersPreference.availableValues.groups.length
        ? undefined
        : filtersPreference.selectedGroups,
    ...(groupIdsFilterActive ? { groupIdsFilterActive } : {}),
    category: categoriesEnabled ? filtersPreference.selectedCategories?.join(',') || undefined : undefined,
    serviceCategory: categoriesEnabled
      ? filtersPreference.selectedServiceCategories?.join(',') || undefined
      : undefined,
    tags: getAvailableListingTagIds(
      filtersPreference.selectedTags,
      filtersPreference.availableValues.tags,
      filtersPreference.featureFlags,
    ),
    search,
    type: forcedFilters?.type ?? type,
    subtypes: (() => {
      const baseSubtypes = forcedFilters?.subtypes ?? filtersPreference.selectedSubtypes ?? [];
      if (
        type === ListingType.offer &&
        isWhiteLabel(WhiteLabel.HauteGaspesie) &&
        !baseSubtypes.includes(ListingSubtype.expirable)
      ) {
        return [...baseSubtypes, ListingSubtype.expirable];
      }
      return baseSubtypes;
    })(),
    hideFoundRequests: forcedFilters?.hideFoundRequests ?? filtersPreference.hideFoundRequests,
    dismissedOnly: forcedFilters?.dismissedOnly ?? filtersPreference.dismissedOnly,
    hideOffersWithFee: filtersPreference.hideOffersWithFee,
    ...(isWhiteLabel(WhiteLabel.HauteGaspesie) &&
      filtersPreference.selectedTransactionModes &&
      filtersPreference.selectedTransactionModes.length > 0 && {
        transactionModes: filtersPreference.selectedTransactionModes,
      }),
    sortBy:
      sortBy ??
      (listsServicesOnly
        ? filtersPreference.sortServices
        : type === ListingType.offer
          ? filtersPreference.sortOffers
          : filtersPreference.sortNeeds),
    isLoggedIn,
    ...(type === ListingType.offer && {
      searchDistance: filtersPreference.searchDistance,
      ...// Don't send searchCenter to API if there's more than one address; backend will then use the addresses' radiusCenter
      (filtersPreference.availableValues.addresses?.length === 1 &&
        filtersPreference.searchCenter && {
          searchCenterLatitude: filtersPreference.searchCenter.radiusCenterLatitude,
          searchCenterLongitude: filtersPreference.searchCenter.radiusCenterLongitude,
        }),
    }),
  };
};

export interface DefaultListingLocation {
  latitude: number;
  longitude: number;
  searchDistance: number;
  maxSearchDistance: number;
}

export function useDefaultListingLocation(opts: FunctionalUseQueryOptions<DefaultListingLocation> = {}) {
  return useQueryLPC<DefaultListingLocation>({
    queryKey: listingKeys.list('default-location'),
    queryFn: () => http.get<DefaultListingLocation>('/listings/default-location').then((res) => res.data),
    ...opts,
  });
}

export function useTopListings(opts: FunctionalUseQueryOptions<Listing[]> = {}) {
  const client = useQueryClient();
  const bffDiscoverScreenLoaded = useAtomValue(bffDiscoverScreenLoadedAtom);

  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const params = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
  };

  const queryKey = listingKeys.list('top');
  return useQueryLPC<Listing[]>({
    queryKey,
    queryFn: () =>
      (bffDiscoverScreenLoaded ? getBffQueryData<Listing[]>(client, queryKey) : undefined) ??
      http.get<Listing[]>('/listings/top', { params }).then((res) => res.data),
    ...opts,
    enabled: (opts.enabled ?? true) && loaded && bffDiscoverScreenLoaded,
  });
}

export function useUrgentNeeds(opts: FunctionalUseQueryOptions<Listing[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  const params: FindAllListingsDTO = {
    type: ListingType.request,
    sortBy: 'urgency' as ListingsSortBy,
    take: 10,
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
    hideFoundRequests: true,
    includeCounts: true,
    dismissedOnly: false,
  };

  const queryKey = listingKeys.list('urgent-needs', params);
  return useQueryLPC<Listing[]>({
    queryKey,
    queryFn: () => http.get<Pagination<Listing>>('/listings', { params }).then((res) => res.data.results),
    ...opts,
    enabled: (opts.enabled ?? true) && isLoggedIn && loaded,
  });
}

export interface ClosestListingsByTag {
  tagId: string;
  listings: Listing[];
}

export function useClosestOffersByTag(tagIds?: string[], opts: FunctionalUseQueryOptions<ClosestListingsByTag[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();
  const params: FindAllListingsDTO = {
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
    tags: tagIds,
    type: ListingType.offer,
    doGrouping: true,
  };

  const queryKey = listingKeys.list('closest_v2', params);
  return useQueryLPC<ClosestListingsByTag[]>({
    queryKey,
    queryFn: () => http.get<ClosestListingsByTag[]>('/listings/closest_v2', { params }).then((res) => res.data),
    ...opts,
    enabled: (opts.enabled ?? true) && isLoggedIn && loaded,
  });
}

interface ListingsByCategoriesResponse {
  results: { categoryId: string; listings: Listing[] }[];
  categories: { id: string; name: string }[];
  nextSkip: number;
  hasMore: boolean;
}

export function useListingsByCategories(opts: FunctionalUseInfiniteQueryOptions<ListingsByCategoriesResponse> = {}) {
  const { showLandingCategories } = useLandingDiscoveryFeatures();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();
  const params: FindAllListingsDTO = {
    searchDistance: MAX_RADIUS_IN_METERS,
    type: ListingType.offer,
    addressIds: selectedAddresses,
    groupIds: selectedGroups,
  };
  const queryKey = listingKeys.list('by_categories_infinite', params);

  return useInfiniteQueryLPC<ListingsByCategoriesResponse>({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await http
        .get('/listings/by_category', {
          params: {
            ...params,
            skipCategories: pageParam,
            takeCategories: 5, // Fetch 5 categories at a time
          },
        })
        .then((res) => res.data);
      return response;
    },
    ...opts,
    enabled: showLandingCategories && isLoggedIn && loaded && (opts.enabled ?? true),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextSkip : undefined),
    initialPageParam: 0,
  });
}

export { normalizeListingFilters } from './listing-query-filters';

export function useFiltersForQuery(filters?: FindAllListingsDTO & { isLoggedIn?: boolean; userLanguage?: Language }) {
  const { data: allGroups } = useAllGroups(true);
  const { data: allAddresses } = useAllAddresses();

  const resolvedFilters = filters ?? {};
  const adjustedFilters = normalizeListingFilters({ ...resolvedFilters }, allGroups, allAddresses);

  const queryKey = listingKeys.list('all', { ...adjustedFilters });

  return {
    adjustedFilters,
    queryKey,
  };
}

export type UseAllListingsOptions = FunctionalUseInfiniteQueryOptions<Pagination<Listing>> & {
  /** When false, skips writing each listing into the detail cache (e.g. count-only overview queries). */
  populateListingDetailCache?: boolean;
};

export function useAllListings(
  filters?: FindAllListingsDTO & { isLoggedIn?: boolean },
  triggeredBy?: string,
  opts: UseAllListingsOptions = {},
  trackSearchPerformedEvent = false,
) {
  const { populateListingDetailCache = true, ...queryOpts } = opts;
  const { data: allGroups } = useAllGroups(true);
  const { data: allAddresses } = useAllAddresses();
  const isStarting = useAtomValue(isStartingAtom);
  const i18nLanguage = getI18nLanguage();

  const optsEnabled =
    (queryOpts.enabled ?? true) &&
    !isStarting &&
    (!filters?.isLoggedIn || (Boolean(allGroups) && Boolean(allAddresses)));

  const { adjustedFilters, queryKey } = useFiltersForQuery({
    ...filters,
    userLanguage: i18nLanguage,
  });

  const queryParams = (() => {
    // isLoggedIn, userLanguage, __counter, etc. are not sent to the server;
    // They were added to filters in order to trigger a refetch when they change.
    const { isLoggedIn: _, userLanguage: __, __counter: ___, ...rest } = adjustedFilters;
    return rest;
  })();

  const client = useQueryClient();
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);

  const query = useInfiniteQueryLPC<Pagination<Listing>>({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const cachedData =
        !pageParam && bffUserProfileLoaded ? getBffQueryData<Pagination<Listing>>(client, queryKey) : undefined;

      if (!cachedData && trackSearchPerformedEvent) {
        track(Event.SearchPerformed, {
          query: adjustedFilters.search!,
          category: adjustedFilters.category,
          serviceCategory: adjustedFilters.serviceCategory,
          searchDistance: adjustedFilters.searchDistance,
          listingType: adjustedFilters.type,
          listingSubtypes: adjustedFilters.subtypes,
          sortBy: adjustedFilters.sortBy,
        });
      }

      const aggregatesPromise =
        Boolean(adjustedFilters.isLoggedIn) && (pageParam ?? 0) === 0
          ? http
              .get<{ totalUnseen: number; numberOfBonifiableListings: number }>('/listings/explore_aggregate_counts', {
                params: queryParams,
              })
              .then((res) => res.data)
              .catch(() => undefined)
          : Promise.resolve(undefined);

      const listingsPagePromise = cachedData
        ? Promise.resolve(cachedData)
        : http
            .get<Pagination<Listing>>('/listings', {
              params: { ...queryParams, skip: pageParam ?? 0, triggeredBy },
            })
            .then((res) => res.data);

      const [listingsPage, aggregatesPayload] = await Promise.all([listingsPagePromise, aggregatesPromise]);

      let resData = listingsPage;
      if (aggregatesPayload) {
        resData = {
          ...resData,
          totalUnseen: aggregatesPayload.totalUnseen,
          numberOfBonifiableListings: aggregatesPayload.numberOfBonifiableListings,
        };
      }

      const searchDistanceUpdated = getSignalData(resData.results[0], Signal.UpdatedSearchDistance);
      if (searchDistanceUpdated) {
        client.setQueryData<Preference<'searchDistance'>>(listingKeys.detail('searchDistance'), {
          name: 'searchDistance',
          value: (searchDistanceUpdated as UpdatedSearchDistanceSignalData).distance,
        });
      }
      return resData;
    },
    ...queryOpts,
    enabled: optsEnabled,
    initialPageParam: 0,
    getNextPageParam: (previousPage) => {
      if (previousPage.skip + previousPage.take < previousPage.total) {
        return previousPage.skip + previousPage.take;
      }
      return undefined;
    },
  });

  useEffect(() => {
    if (!populateListingDetailCache) return;
    if (query.error) return;
    if (!query.data) return;
    query.data.pages.forEach((page) => {
      page.results.forEach((listing) => {
        client.setQueryData<Listing>(listingKeys.detail(listing.id), (prev) => {
          if (!hasListingUser(prev)) return { ...listing };

          const merged = mergeListingDetails(prev, listing);

          if (
            prev.numViews === merged.numViews &&
            prev.numInteractions === merged.numInteractions &&
            prev.numFavourites === merged.numFavourites &&
            prev.user.trustPoints === merged.user.trustPoints &&
            prev.lender === merged.lender &&
            prev.lenders === merged.lenders &&
            prev.additionalLendersCount === merged.additionalLendersCount
          ) {
            return prev;
          }

          return merged;
        });
      });
    });
  }, [client, populateListingDetailCache, query.data, query.error]);

  const filteredData =
    query.data && adjustedFilters.doGrouping !== false
      ? {
          ...query.data,
          pages: filterGroupedListings(query.data.pages),
        }
      : query.data;

  return { ...query, data: filteredData } as typeof query;
}

function filterGroupedListings(pages: Pagination<Listing>[]) {
  const groupedListingIds = new Set<Listing['id']>();
  const groupingIds = new Set<ListingGrouping['id']>();
  return pages.map((page) => {
    if (page.skip === 0) {
      groupedListingIds.clear();
      groupingIds.clear();
    }

    const results: Listing[] = [];
    page.results.forEach((listing) => {
      if (
        listing.grouping?.listings.length &&
        (groupingIds.has(listing.grouping.id) || groupedListingIds.has(listing.id))
      ) {
        return;
      }

      // Only a retained representative may suppress its grouped siblings.
      if (listing.grouping?.listings.length) {
        // Previews contain at most four listings; use the group ID for later representatives.
        if (listing.grouping.id) groupingIds.add(listing.grouping.id);
        groupedListingIds.add(listing.id);
        listing.grouping.listings.forEach((grouped) => groupedListingIds.add(grouped.id));
      }

      results.push(
        listing.grouping?.listings.length
          ? {
              ...listing,
              distance: listing.grouping.listings.reduce(
                (minimum, grouped) => Math.min(minimum, grouped.distance ?? Infinity),
                listing.distance ?? 0,
              ),
            }
          : listing,
      );
    });

    return { ...page, results };
  });
}

const roundMapNumber = (value: number, precision = 4) => Number(value.toFixed(precision));

function roundMapViewport(viewport?: ListingMapViewport): ListingMapViewport | undefined {
  if (!viewport) return undefined;
  return {
    north: roundMapNumber(viewport.north),
    south: roundMapNumber(viewport.south),
    east: roundMapNumber(viewport.east),
    west: roundMapNumber(viewport.west),
    zoom: Math.round(viewport.zoom),
  };
}

function getMapDataIdentity(queryParams: FindListingsMapDTO) {
  const { east: _east, north: _north, south: _south, west: _west, zoom: _zoom, ...identity } = queryParams;
  return identity;
}

function getMapQueryParams(queryKey: readonly unknown[]): FindListingsMapDTO | undefined {
  const descriptor = queryKey.at(-1);
  if (!descriptor || typeof descriptor !== 'object' || !('filters' in descriptor)) return undefined;
  return (descriptor as { filters?: FindListingsMapDTO }).filters;
}

export function useListingMap(
  filters?: FindAllListingsDTO & { cluster?: 'true' | 'false'; isLoggedIn?: boolean },
  viewport?: ListingMapViewport,
  opts: FunctionalUseQueryOptions<ListingsMapResponse> = {},
) {
  const isStarting = useAtomValue(isStartingAtom);
  const i18nLanguage = getI18nLanguage();
  const { adjustedFilters } = useFiltersForQuery({
    ...filters,
    userLanguage: i18nLanguage,
  });
  const cluster = filters?.cluster ?? 'true';
  const roundedViewport = roundMapViewport(viewport);
  const queryParams: FindListingsMapDTO | undefined = (() => {
    if (!roundedViewport) return undefined;
    const { isLoggedIn: _, userLanguage: __, __counter: ___, ...rest } = adjustedFilters;
    return { ...rest, doGrouping: false, ...roundedViewport, cluster, clusteringVersion: 2, limit: 5000 };
  })();
  const requestedPlaceholderData = opts.placeholderData;
  const compatiblePlaceholderData = (
    previousData: ListingsMapResponse | undefined,
    previousQuery: { queryKey: readonly unknown[] } | undefined,
  ) => {
    if (requestedPlaceholderData === undefined || !queryParams || !previousQuery) return undefined;
    const previousQueryParams = getMapQueryParams(previousQuery.queryKey);
    if (
      !previousQueryParams ||
      !objectsAreEquivalent(getMapDataIdentity(previousQueryParams), getMapDataIdentity(queryParams))
    ) {
      return undefined;
    }
    return typeof requestedPlaceholderData === 'function'
      ? requestedPlaceholderData(previousData, previousQuery as never)
      : requestedPlaceholderData;
  };

  return useQueryLPC<ListingsMapResponse>({
    queryKey: listingKeys.map(queryParams),
    queryFn: ({ signal }) =>
      queryParams
        ? http.get<ListingsMapResponse>('/listings/map', { params: queryParams, signal }).then((res) => res.data)
        : Promise.resolve({ clusters: [], listings: [], total: 0 }),
    ...opts,
    placeholderData: compatiblePlaceholderData,
    enabled: (opts.enabled ?? true) && !isStarting && Boolean(filters?.isLoggedIn) && Boolean(queryParams),
  });
}

function updateListingDetailFavouriteCache(
  client: ReturnType<typeof useQueryClient>,
  listingId: Listing['id'],
  isFavourite: boolean,
  serverListing?: Listing,
) {
  client.setQueryData<Listing>(listingKeys.detail(listingId), (previous) => {
    const next = serverListing
      ? hasListingUser(previous)
        ? mergeListingDetails(previous, serverListing)
        : serverListing
      : previous;
    if (!next) return previous;

    const wasFavourite = (previous?.favourites?.length ?? 0) > 0;
    const countAdjustment = Number(isFavourite) - Number(wasFavourite);
    const numFavourites =
      (serverListing?.numFavourites ?? previous?.numFavourites === undefined)
        ? next.numFavourites
        : Math.max(0, previous.numFavourites + countAdjustment);

    return {
      ...next,
      favourites: isFavourite ? (serverListing?.favourites ?? next.favourites) : undefined,
      numFavourites,
    };
  });
}

export function useAddOneListingToFavourites(opts: UseMutationOptions<Listing, Error, CreateFavouriteDTO> = {}) {
  const client = useQueryClient();
  return useMutation<Listing, Error, CreateFavouriteDTO>({
    mutationFn: (payload) => http.post<Listing>('/listings/favourites', payload).then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      track(Event.FavoriteListing, {
        listing_id: result.id,
        listing_type: result.type,
        listing_category: result.categoryPrimary.id,
        is_quick_offer: isQuickOffer(result),
      });
      trackOnFacebook(CustomFacebookEvent.Contributed, { contribution_type: ContributionType.Favourite });
      updateListingDetailFavouriteCache(client, variables.listingId, true, result);
      client.setQueryData<Listing[]>(listingKeys.list('favourites'), (previous) =>
        previous && !previous.some((listing) => listing.id === result.id) ? [result, ...previous] : previous,
      );
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useAddOneGroupingToFavourites(
  opts: UseMutationOptions<ListingGrouping, Error, CreateGroupingFavouriteDTO> = {},
) {
  const client = useQueryClient();
  return useMutation<ListingGrouping, Error, CreateGroupingFavouriteDTO>({
    mutationFn: ({ groupingId }) =>
      http.post<ListingGrouping>(`/listings/favourites/groupings`, { groupingId }).then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      if (result.listings.length > 0) {
        const eventProperties = {
          grouping_id: variables.groupingId,
          listing_category: result.listings[0]!.categoryPrimary.id,
          item_name: result.listings[0]!.item.name,
        };
        track(Event.FavoriteGrouping, eventProperties);
      }
      trackOnFacebook(CustomFacebookEvent.Contributed, { contribution_type: ContributionType.Favourite });
      updateGroupingFavouriteCache(client, variables.groupingId, result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useAllFavourites(opts: Omit<UndefinedInitialDataOptions<Listing[]>, 'queryKey' | 'queryFn'> = {}) {
  const client = useQueryClient();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const query = useQuery<Listing[]>({
    queryKey: listingKeys.list('favourites'),
    queryFn: () => http.get<Listing[]>('/listings/favourites').then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true),
  });

  useEffect(() => {
    if (query.data) {
      client.setQueryData(listingKeys.list('favourites'), query.data);
    }
  }, [client, query.data]);

  return query;
}

export function useCreateListing(opts: UseMutationOptions<Listing, Error, CreateListingDTO> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);
  const newListingSource = useAtomValue(newListingSourceAtom);
  const resetNewListingSource = useResetAtom(newListingSourceAtom);
  const mutate: MutationFunction<Listing, CreateListingDTO> = async (payload) => {
    const data = {
      addressIds: payload.addressIds,
      categoryPrimaryId: payload.categoryPrimaryId,
      serviceCategoryId: payload.serviceCategoryId,
      description: payload.description,
      end: payload.end,
      groupIds: payload.groupIds,
      itemName: payload.item.name,
      picture: payload.picture,
      pictures: payload.pictures,
      pictureUrl: payload.pictureUrl,
      start: payload.start,
      type: payload.type,
      subtype: payload.subtype,
      customCenterLatitude: payload.customCenterLatitude,
      customCenterLongitude: payload.customCenterLongitude,
      customDistance: payload.customDistance,
      quantity: isWhiteLabel(WhiteLabel.HauteGaspesie) ? undefined : payload.quantity,
      estimatedPrice: payload.estimatedPrice,
      ...getListingTransactionFields(payload, isWhiteLabel(WhiteLabel.HauteGaspesie)),
      source: payload.source ?? newListingSource,
      blockedDates: payload.blockedDates,
      loanFee: payload.loanFee,
      loanFeeDescription: payload.loanFeeDescription,
      loanFeePaymentMethod: payload.loanFeePaymentMethod,
      loanFeeDocument: payload.loanFeeDocument === null ? 'null' : payload.loanFeeDocument,
      timeStart: payload.timeStart,
      timeEnd: payload.timeEnd,
      address: getAddressPayload(payload.address),
      maxParticipants: payload.maxParticipants,
      automaticMessage: payload.automaticMessage,
      urgent: payload.subtype === ListingSubtype.expirable ? Boolean(payload.urgent) : undefined,
      duplicates: payload.duplicates,
    };

    return http.post<Listing>('/listings', asFormData(data)).then((res) => res.data);
  };

  return useMutation({
    mutationFn: mutate,
    ...opts,
    onSuccess(result, variables, context) {
      mergeListingDetailCache(client, result);

      setSelfUserLoaded(false);

      void qk.afterListingCreated(result);

      trackOnFacebook(CustomFacebookEvent.Contributed, {
        contribution_type:
          result.type === ListingType.offer ? ContributionType.PublishedOffer : ContributionType.PublishedRequest,
      });

      resetNewListingSource();
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUpdateOneListing(
  id: Listing['id'],
  opts: UseMutationOptions<Listing, Error, UpdateListingDTO> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const mutate: MutationFunction<Listing, UpdateListingDTO> = (payload) => {
    const data = {
      addressIds: payload.addressIds,
      categoryPrimaryId: payload.categoryPrimaryId,
      serviceCategoryId: payload.serviceCategoryId,
      description: payload.description,
      groupIds: payload.groupIds,
      ...(payload.item && {
        item: {
          name: payload.item.name,
        },
      }),
      status: payload.status,
      subtype: payload.subtype,
      end: payload.end,
      start: payload.start,
      customCenterLatitude: payload.customCenterLatitude,
      customCenterLongitude: payload.customCenterLongitude,
      customDistance: payload.customDistance,
      quantity: isWhiteLabel(WhiteLabel.HauteGaspesie) ? undefined : payload.quantity,
      estimatedPrice: payload.estimatedPrice,
      ...getListingTransactionFields(payload, isWhiteLabel(WhiteLabel.HauteGaspesie)),
      loanFee: payload.loanFee,
      loanFeeDescription: payload.loanFeeDescription,
      loanFeePaymentMethod: payload.loanFeePaymentMethod,
      loanFeeDocument: payload.loanFeeDocument === null ? 'null' : payload.loanFeeDocument,
      timeStart: payload.timeStart,
      timeEnd: payload.timeEnd,
      address: getAddressPayload(payload.address),
      maxParticipants: payload.maxParticipants,
      automaticMessage: payload.automaticMessage,
      urgent:
        payload.subtype === ListingSubtype.expirable
          ? Boolean(payload.urgent)
          : payload.urgent === true
            ? false
            : payload.urgent,
      conciergerieDisabledByUser: payload.conciergerieDisabledByUser,
      duplicates: payload.duplicates,
    };

    return http
      .patch<Listing>(`/listings/${id}`, payload.urgent === undefined ? asFormData(data) : data)
      .then((res) => res.data);
  };

  return useMutation({
    mutationFn: mutate,
    ...opts,
    // Why would we want to manually update the query cache here, just before the mutation is called?
    // It will be updated in the onSuccess callback below, in a few ms...
    // onMutate(variables) {
    //   const existing = getValidQueryData<Listing>(client, keys.detail(id));
    //
    //   if (existing) {
    //     // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    //     client.setQueryData<Listing>(keys.detail(id), {
    //       ...existing,
    //       addresses: variables.addressIds?.map((addressId) => ({ id: addressId })) ?? existing.addresses,
    //       description: variables.description ?? existing.description,
    //       groups: variables.groupIds?.map((groupId) => ({ id: groupId })) ?? existing.groups,
    //       item: {
    //         name: variables.item?.name ?? existing.item.name,
    //       },
    //       status: variables.status ?? existing.status,
    //       ...((existing.type === ListingType.request && {
    //         end: variables.end ?? existing.end,
    //         start: variables.start ?? existing.start,
    //       }) as any),
    //     });
    //   }
    //
    //   return opts.onMutate?.(variables);
    // },
    async onSuccess(result, variables, context) {
      const key = listingKeys.detail(result.id);
      client.setQueryData(key, (previous) => {
        if (!hasListingUser(previous)) {
          return result;
        }

        return mergeListingDetails(previous, result);
      });

      await qk.allListingLists.invalidate();

      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useAddListingPictures(
  listingId: Listing['id'],
  postId?: string,
  opts: UseMutationOptions<ListingPicture[], Error, ImagePickerAsset[]> = {},
) {
  const qk = useQueryKeys();

  return useMutation({
    mutationFn: async (images: ImagePickerAsset[]) => {
      if (images.length === 0) return [];
      return await http
        .post(`/listings/${listingId}/pictures`, asFormData({ pictures: images }), { params: { postId } })
        .then((res) => res.data);
    },
    ...opts,
    onSuccess(result, variables, context) {
      void qk.listing.invalidate(listingId);
      void qk.allListingLists.invalidate();
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useDeleteListingPictures(
  listingId: Listing['id'],
  postId?: string,
  opts: UseMutationOptions<void, Error, ListingPicture['id'][]> = {},
) {
  const qk = useQueryKeys();

  return useMutation({
    mutationFn: async (pictureIds: ListingPicture['id'][]) => {
      if (pictureIds.length === 0) {
        return;
      }
      await http
        .delete(`/listings/${listingId}/pictures`, { data: { pictureIds }, params: { postId } })
        .then(() => undefined);
    },
    ...opts,
    onSuccess(result, variables, context) {
      void qk.listing.invalidate(listingId);
      void qk.allListingLists.invalidate();
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUpdateListingPicture(
  listingId: Listing['id'],
  pictureId: ListingPicture['id'],
  postId?: string,
  opts: UseMutationOptions<ListingPicture, Error, ImagePickerAsset> = {},
) {
  const qk = useQueryKeys();

  return useMutation({
    mutationFn: async (image: ImagePickerAsset) => {
      return await http
        .patch(`/listings/${listingId}/pictures/${pictureId}`, asFormData({ picture: image }), { params: { postId } })
        .then((res) => res.data);
    },
    ...opts,
    onSuccess(result, variables, context) {
      void qk.listing.invalidate(listingId);
      void qk.allListingLists.invalidate();
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

const viewedListingIds = new Set<Listing['id']>();

export function useOneListing(
  id: Listing['id'] | undefined,
  screen: string,
  opts: FunctionalUseQueryOptions<Listing> = {},
  location?: Pick<FindAllListingsDTO, 'searchCenterLatitude' | 'searchCenterLongitude' | 'searchDistance'>,
) {
  const client = useQueryClient();
  const keys = useGetQueryKeys();

  return useQuery<Listing>({
    queryKey: listingKeys.detailForScreen(id!, screen, location),
    queryFn: async () => {
      const data = await http
        .get<Listing>(`/listings/${id!}`, { params: { screen, ...location } })
        .then((res) => res.data);

      if (screen === 'one-listing' && !viewedListingIds.has(id!) && Number.isInteger(data.numViews)) {
        const selfUser = client.getQueryData<User>(keys.selfUser());
        if (selfUser && data.userId !== selfUser.id) {
          data.numViews = data.numViews! + 1;
          viewedListingIds.add(id!);
        }
      }

      // Prevent counter regression: keep the highest known value
      const cached = client.getQueryData<Listing>(listingKeys.detail(id!));
      return hasListingUser(cached) ? mergeListingDetails(cached, data) : data;
    },
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(id),
  });
}

/**
 * Subscribes to the detail cache for a listing and returns fresh counter values.
 * Used by listing cards so they reactively pick up counters updated by useOneListing,
 * without needing to refetch or patch the list cache.
 */
export function useFreshListingCounters(id: Listing['id']) {
  const { data } = useQuery({
    queryKey: listingKeys.detail(id),
    queryFn: skipToken,
    enabled: false,
    select: (d: Listing) => {
      const user = (d as { user?: Listing['user'] }).user;
      return {
        numViews: d.numViews,
        numInteractions: d.numInteractions,
        numFavourites: d.numFavourites,
        trustPoints: user?.trustPoints,
      };
    },
  });
  return data;
}

export function useOneListingLenders(id: Listing['id'], opts: FunctionalUseQueryOptions<Listing['lender'][]> = {}) {
  return useQuery<Listing['lender'][]>({
    queryKey: listingKeys.lenders(id),
    queryFn: () => http.get<Listing['lender'][]>(`/listings/${id}/lenders`).then((res) => res.data),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(id),
  });
}

export function useListingBorrowers(id: Listing['id'], opts: FunctionalUseQueryOptions<ListingBorrower[]> = {}) {
  return useQuery<ListingBorrower[]>({
    queryKey: listingKeys.borrowers(id),
    queryFn: () => http.get<ListingBorrower[]>(`/listings/${id}/borrowers`).then((res) => res.data),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(id),
  });
}

export function useManyListing(id: Listing['id'][], screen: string, opts: FunctionalUseQueryOptions<Listing[]> = {}) {
  const client = useQueryClient();
  return useQuery<Listing[]>({
    queryKey: listingKeys.detailMany(id.join(',')),
    queryFn: async () => {
      if (id.length === 0) {
        return [];
      }
      const res = await http.get<Listing | Listing[]>(`/listings/${id.join(',')}?screen=${encodeURIComponent(screen)}`);
      const listingsArray = Array.isArray(res.data) ? res.data : [res.data];
      listingsArray.forEach((listing) => mergeListingDetailCache(client, listing));
      return listingsArray;
    },
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(id),
  });
}

interface ListingDistanceResponse {
  distance: number;
  distanceFrom?: { name?: string; icon?: number };
  city: string;
  publicLocation?: PublicLocation;
  publicLocations?: PublicLocation[];
}

export function useOneListingDistance(
  id: Listing['id'],
  opts: FunctionalUseQueryOptions<ListingDistanceResponse> = {},
) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();
  const queryParameters = (() => {
    return new URLSearchParams({
      groups: selectedGroups.join(','),
      addresses: selectedAddresses.join(','),
    }).toString();
  })();
  return useQuery<ListingDistanceResponse>({
    queryKey: listingKeys.detail(`${id}/distance?${queryParameters}`),
    queryFn: () =>
      http.get<ListingDistanceResponse>(`/listings/${id}/distance?${queryParameters}`).then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true) && loaded,
  });
}

type PossibleLender = User & { profile: Profile; lastMessageWhen: Date; aboutListing: boolean };

export function useOneListingPossibleLenders(
  id: Listing['id'],
  opts: FunctionalUseQueryOptions<PossibleLender[]> = {},
) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  return useQuery<PossibleLender[]>({
    queryKey: listingKeys.detail(`${id}/possible_lenders`),
    queryFn: () => http.get<PossibleLender[]>(`/listings/${id}/possible_lenders`).then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true),
  });
}

export const buildDefaultGroupIds = (groups?: (Group | Address)[]) => groups?.map((ga) => ga.id);

export function useDefaultGroupsAndAddresses() {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { data: groups } = useAllGroups(true);
  const { data: addresses } = useAllAddresses();
  const defaultGroups = !isLoggedIn ? [] : buildDefaultGroupIds(groups);
  const defaultAddresses = !isLoggedIn ? [] : buildDefaultGroupIds(addresses);
  return {
    defaultGroups,
    defaultAddresses,
    groupsLoaded: groups !== undefined,
    addressesLoaded: addresses !== undefined,
  };
}

export function useSelectedGroupsAndAddresses(
  groupsState?: [Group['id'][] | undefined, Dispatch<SetStateAction<Group['id'][] | undefined>>],
  addressesState?: [Address['id'][] | undefined, Dispatch<SetStateAction<Address['id'][] | undefined>>],
  excludeMunicipal = true,
) {
  const { defaultGroups, defaultAddresses, groupsLoaded, addressesLoaded } = useDefaultGroupsAndAddresses();
  const { data: groups } = useAllGroups(excludeMunicipal);

  const [selectedGroups, setSelectedGroups] = useStoredValue<string[]>(
    StorageKey.ListingsFiltersGroups,
    defaultGroups,
    groupsState,
    true,
    groupsLoaded,
  );

  const [selectedAddresses, setSelectedAddresses, usedDefault] = useStoredValue<Address['id'][]>(
    StorageKey.ListingsFiltersAddresses,
    defaultAddresses,
    addressesState,
    true,
    addressesLoaded,
  );

  const loaded =
    selectedGroups !== undefined &&
    selectedAddresses !== undefined &&
    (selectedGroups.length > 0 || selectedAddresses.length > 0);

  // If the user is in a municipal_closed group, default to only the group being selected
  const [finishedLoading, setFinishedLoading] = useState(false);
  useEffect(() => {
    if (!addressesLoaded || !groups || !loaded || finishedLoading) return;
    const timeout = setTimeout(
      () => {
        if (
          selectedAddresses.length > 0 &&
          usedDefault &&
          groups.some((group) => group.subtype === 'municipal_closed')
        ) {
          setSelectedAddresses([]);
        }
        setFinishedLoading(true);
      },
      selectedAddresses.length === 0 ? 0 : 10,
    );
    return () => clearTimeout(timeout);
  }, [addressesLoaded, finishedLoading, groups, loaded, selectedAddresses, setSelectedAddresses, usedDefault]);

  return {
    selectedGroups: selectedGroups ?? [],
    selectedAddresses: selectedAddresses ?? [],
    setSelectedGroups,
    setSelectedAddresses,
    loaded: loaded && finishedLoading,
  };
}

export function useManyListingsDistances(
  ids: Listing['id'][],
  opts: FunctionalUseQueryOptions<(ListingDistanceResponse & { id: string; trustPoints?: number })[]> = {},
) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();
  const queryParameters = (() => {
    return new URLSearchParams({
      groups: selectedGroups.join(','),
      addresses: selectedAddresses.join(','),
    }).toString();
  })();
  return useQuery<(ListingDistanceResponse & { id: string; trustPoints?: number })[]>({
    queryKey: listingKeys.detail(`${ids.join(',')}/distance?${queryParameters}`),
    queryFn: () =>
      http
        .get<(ListingDistanceResponse & { id: string; trustPoints?: number })[]>(
          `/listings/${ids.join(',')}/distance?${queryParameters}`,
        )
        .then((res) => {
          if (!Array.isArray(res.data)) {
            return [res.data];
          }
          return res.data;
        }),
    ...opts,
    enabled: (opts.enabled ?? true) && isLoggedIn && loaded && ids.length > 0,
  });
}

export function useRemoveOneListingFromFavourites(opts: UseMutationOptions<Favorite, Error, Listing['id']> = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => http.delete<Favorite>(`/listings/favourites/${id}`).then((res) => res.data),
    ...opts,
    onSuccess(result, id, context) {
      track(Event.UnfavoriteListing, { listing_id: result.listingId });
      updateListingDetailFavouriteCache(client, id, false);
      client.setQueryData<Listing[]>(listingKeys.list('favourites'), (previous) =>
        previous?.filter((listing) => listing.id !== id),
      );
      return opts.onSuccess?.(result, id, context);
    },
  });
}

export function useRemoveOneGroupingFromFavourites(
  opts: UseMutationOptions<FavouriteGrouping, Error, ListingGrouping['id']> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      http.delete<FavouriteGrouping>(`/listings/favourites/groupings/${id}`).then((res) => res.data),
    ...opts,
    onSuccess(_result, id, context) {
      track(Event.UnfavoriteGrouping, { grouping_id: id });
      updateGroupingFavouriteCache(client, id);
      return opts.onSuccess?.(_result, id, context);
    },
  });
}

export function useCreateQuickListings(opts: UseMutationOptions<Listing[], Error, CreateListingsDTO> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);
  const newListingSource = useAtomValue(newListingSourceAtom);
  const resetNewListingSource = useResetAtom(newListingSourceAtom);
  const mutate: MutationFunction<Listing[], CreateListingsDTO> = async (payload) => {
    const enrichedPayload = {
      ...payload,
      listings: payload.listings.map((listing) => ({
        ...listing,
        itemName: listing.item.name,
        source: listing.source ?? newListingSource,
      })),
    };
    return http.post<Listing[]>('/listings/multiple', asFormData(enrichedPayload)).then((res) => res.data);
  };

  return useMutation({
    mutationFn: mutate,
    ...opts,
    onSuccess(result, variables, context) {
      for (const listing of result) {
        mergeListingDetailCache(client, listing);
      }

      setSelfUserLoaded(false);

      void qk.afterListingCreated(result[result.length - 1]!);

      trackOnFacebook(CustomFacebookEvent.Contributed, { contribution_type: ContributionType.PublishedQuickOffers });

      resetNewListingSource();
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

// When a listing uses a picture URL in our S3 bucket, in the /default/media/ folder, that is a quick-offer
export const isQuickOffer = (listing?: Pick<Listing, 'picture' | 'type'>) => {
  return listing?.type === ListingType.offer && Boolean(listing.picture?.includes('/default/media/'));
};

export function useTrackNewListingSource() {
  const setNewListingSource = useSetAtom(newListingSourceAtom);
  return (source: NewListingSource) => {
    setNewListingSource(source);
  };
}

export function useBlockDatesForListings(
  ids: Listing['id'][],
  opts: UseMutationOptions<Listing[], Error, Date[]> = {},
) {
  const client = useQueryClient();
  const mutate: MutationFunction<Listing[], Date[]> = async (dates) =>
    ids.length === 0
      ? []
      : await http
          .post<Listing[]>(`/listings/${ids.join(',')}/availability`, { blockedDates: dates })
          .then((res) => res.data);

  return useMutation({
    mutationFn: mutate,
    ...opts,
    onSuccess(updatedListings, variables, context) {
      updatedListings.forEach((listing) => mergeListingDetailCache(client, listing));
      return opts.onSuccess?.(updatedListings, variables, context);
    },
  });
}

export type BatchUpdateListingLoanFeeDTO = Pick<
  Listing,
  'loanFee' | 'loanFeeDescription' | 'loanFeePaymentMethod' | 'loanFeeDocument'
>;

export function useLoanFeeForListings(
  ids: Listing['id'][],
  opts: UseMutationOptions<Listing[], Error, BatchUpdateListingLoanFeeDTO> = {},
) {
  const client = useQueryClient();
  const mutate: MutationFunction<Listing[], BatchUpdateListingLoanFeeDTO> = async (payload) =>
    ids.length === 0
      ? []
      : await http.post<Listing[]>(`/listings/${ids.join(',')}/loan-fee`, payload).then((res) => res.data);

  return useMutation({
    mutationFn: mutate,
    ...opts,
    onSuccess(updatedListings, variables, context) {
      updatedListings.forEach((listing) => mergeListingDetailCache(client, listing));
      return opts.onSuccess?.(updatedListings, variables, context);
    },
  });
}

export function useGetOneLoadedListing() {
  const client = useQueryClient();
  return (id: Listing['id']) => client.getQueryData<Listing>(listingKeys.detail(id));
}

export function useBatchHideListings(
  opts: UseMutationOptions<void, Error, Listing['id'][]> & { invalidateFilters?: FindAllListingsDTO } = {},
) {
  const qk = useQueryKeys();
  const client = useQueryClient();
  const mutate: MutationFunction<void, Listing['id'][]> = (ids) =>
    http.post(`/listings/batch-hide`, ids).then((res) => res.data);
  return useMutation({
    mutationFn: mutate,
    ...opts,
    async onSuccess(_, variables, context) {
      await Promise.all(variables.map((id) => client.invalidateQueries({ queryKey: listingKeys.detail(id) })));
      await qk.myOffers.invalidate();
      if (opts.invalidateFilters) {
        await qk.listingsWithFilters(opts.invalidateFilters);
      }
      return opts.onSuccess?.(_, variables, context);
    },
  });
}

export function useBatchUnhideListings(
  opts: UseMutationOptions<void, Error, Listing['id'][]> & { invalidateFilters?: FindAllListingsDTO } = {},
) {
  const qk = useQueryKeys();
  const client = useQueryClient();
  const mutate: MutationFunction<void, Listing['id'][]> = (ids) =>
    http.post(`/listings/batch-unhide`, ids).then((res) => res.data);
  return useMutation({
    mutationFn: mutate,
    ...opts,
    async onSuccess(_, variables, context) {
      await Promise.all(variables.map((id) => client.invalidateQueries({ queryKey: listingKeys.detail(id) })));
      await qk.myOffers.invalidate();
      if (opts.invalidateFilters) {
        await qk.listingsWithFilters(opts.invalidateFilters);
      }
      return opts.onSuccess?.(_, variables, context);
    },
  });
}

export function useBatchDeleteListings(
  opts: UseMutationOptions<void, Error, Listing['id'][]> & { invalidateFilters?: FindAllListingsDTO } = {},
) {
  const qk = useQueryKeys();
  const client = useQueryClient();
  const mutate: MutationFunction<void, Listing['id'][]> = (ids) =>
    http.post(`/listings/batch-delete`, ids).then((res) => res.data);
  return useMutation({
    mutationFn: mutate,
    ...opts,
    async onSuccess(_, variables, context) {
      await Promise.all(variables.map((id) => client.invalidateQueries({ queryKey: listingKeys.detail(id) })));
      await qk.myOffers.invalidate();
      if (opts.invalidateFilters) {
        await qk.listingsWithFilters(opts.invalidateFilters);
      }
      return opts.onSuccess?.(_, variables, context);
    },
  });
}

interface BatchDuplicateListingsDTO {
  listingIds: Listing['id'][];
  count: number;
}

export function useBatchDuplicateListings(
  opts: UseMutationOptions<{ listingIds: Listing['id'][] }, Error, BatchDuplicateListingsDTO> & {
    invalidateFilters?: FindAllListingsDTO;
  } = {},
) {
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: (payload) =>
      http.post<{ listingIds: Listing['id'][] }>('/listings/batch-duplicate', payload).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      await qk.myOffers.invalidate();
      if (opts.invalidateFilters) await qk.listingsWithFilters(opts.invalidateFilters);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

interface BatchUpdateListingsPriceDTO {
  listingIds: Listing['id'][];
  price: string;
}

export function useBatchUpdateListingsPrice(
  opts: UseMutationOptions<void, Error, BatchUpdateListingsPriceDTO> & { invalidateFilters?: FindAllListingsDTO } = {},
) {
  const qk = useQueryKeys();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload) => http.patch('/listings/batch-price', payload).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      await Promise.all(
        variables.listingIds.map((id) => client.invalidateQueries({ queryKey: listingKeys.detail(id) })),
      );
      await qk.myOffers.invalidate();
      if (opts.invalidateFilters) await qk.listingsWithFilters(opts.invalidateFilters);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}
export type TagStatus = 'draft' | 'published' | 'archived';

export interface TagCollection {
  id: string;
  // extend as needed
}

export interface ListingsTags {
  listingId: string;
  tagId: string;
  // extend as needed
}

export interface PopularItemsTags {
  itemId: string;
  tagId: string;
  // extend as needed
}

export interface Tag {
  id: string;
  tagName: string;
}
export function useAllTags(sortByPopularity = false, opts: FunctionalUseQueryOptions<Tag[]> = {}) {
  const { tagsEnabled } = useListingCatalog();
  return useQuery<Tag[]>({
    queryKey: listingKeys.list(
      `tags-${String(sortByPopularity)}${isWhiteLabel(WhiteLabel.Mutuo) ? '-mutuo-catalog-v1' : ''}`,
    ), // key differs per sortByPopularity value
    queryFn: () => http.get<Tag[]>('/listings/tags', { params: { sortByPopularity } }).then((res) => res.data),
    ...opts,
    enabled: tagsEnabled && (opts.enabled ?? true),
  });
}

export function useReportListing(opts: UseMutationOptions<unknown, Error, Listing['id']> = {}) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, Listing['id']>({
    mutationFn: (listingId: Listing['id']) =>
      http.post<unknown>(`/listings/${listingId}/report`).then((res) => res.data),
    ...opts,
    onSuccess: async (data, variables, context) => {
      queryClient.removeQueries({ queryKey: listingKeys.lists() });
      await queryClient.invalidateQueries({ queryKey: listingKeys.all });
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useAvailableSubtypes() {
  const userInfo = useAtomValue(userInfoAtom);
  const canUseServices = useCanUseServices();
  const canUseDonations = Boolean(userInfo?.canPublishDonations);
  return [
    ListingSubtype.object,
    ...(canUseServices ? [ListingSubtype.service] : []),
    ...(canUseDonations ? [ListingSubtype.donation] : []),
  ];
}

function optimisticallyRemoveListingFromCaches(client: ReturnType<typeof useQueryClient>, listingId: Listing['id']) {
  const updater = (old: unknown) => {
    if (Array.isArray(old)) {
      return old.filter((l: Listing) => l.id !== listingId);
    }
    const paginated = old as { pages: Pagination<Listing>[]; pageParams: unknown[] } | undefined;
    if (!paginated?.pages) return old;
    const foundInAnyPage = paginated.pages.some((p) => p.results.some((l) => l.id === listingId));
    const totalAdjustment = foundInAnyPage ? 1 : 0;
    return {
      ...paginated,
      pages: paginated.pages.map((page) => ({
        ...page,
        results: page.results.filter((l) => l.id !== listingId),
        total: page.total - totalAdjustment,
      })),
    };
  };
  client.setQueriesData({ queryKey: listingKeys.lists() }, updater);
  client.setQueriesData({ queryKey: recommendationKeys.lists() }, updater);
}

export function useDismissListing(opts: UseMutationOptions<{ success: true }, Error, Listing['id']> = {}) {
  const client = useQueryClient();
  return useMutation<{ success: true }, Error, Listing['id']>({
    mutationFn: (listingId) =>
      http.post<{ success: true }>('/listings/dismissals', { listingId }).then((res) => res.data),
    ...opts,
    onMutate: (listingId) => {
      // Snapshot all listing list queries for rollback
      const previousQueries = [
        ...client.getQueriesData({ queryKey: listingKeys.lists() }),
        ...client.getQueriesData({ queryKey: recommendationKeys.lists() }),
      ];

      // Optimistically remove the listing from all list caches
      optimisticallyRemoveListingFromCaches(client, listingId);

      opts.onMutate?.(listingId);
      return { previousQueries };
    },
    onError: (error, listingId, context) => {
      // Rollback on error
      const ctx = context as { previousQueries: [queryKey: unknown[], data: unknown][] } | undefined;
      ctx?.previousQueries.forEach(([queryKey, data]) => {
        client.setQueryData(queryKey as string[], data);
      });
      return opts.onError?.(error, listingId, context);
    },
    onSuccess: async (data, variables, context) => {
      // Mark all list caches as stale without triggering immediate refetches;
      // the optimistic update remains visible and stale caches refresh when next viewed.
      await Promise.all([
        client.invalidateQueries({ queryKey: listingKeys.lists(), refetchType: 'none' }),
        client.invalidateQueries({ queryKey: recommendationKeys.lists(), refetchType: 'none' }),
      ]);
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export interface AnalyzeListingResponse {
  title: string;
  description: string;
  category: string;
  suggestedPrice: number;
}

export function useAnalyzeListingImage(opts: UseMutationOptions<AnalyzeListingResponse, Error, ImagePickerAsset> = {}) {
  return useMutation({
    mutationFn: async (image: ImagePickerAsset) =>
      http.post<AnalyzeListingResponse>('/listings/analyze', asFormData({ image })).then((res) => res.data),
    ...opts,
  });
}

export const BULK_LISTING_MAX_PHOTOS = 30;
export const BULK_LISTING_UPLOAD_CHUNK_SIZE = 10;

export type BulkListingSessionStatus = 'drafting' | 'generating' | 'reviewing' | 'completed' | 'cancelled' | 'expired';

export type BulkListingDraftStatus =
  | 'uploaded'
  | 'queued'
  | 'generating'
  | 'generated'
  | 'generation_failed'
  | 'skipped'
  | 'saving'
  | 'saved'
  | 'save_failed'
  | 'deleted';

export interface BulkListingProgress {
  total: number;
  uploaded: number;
  queued: number;
  generating: number;
  generated: number;
  generationFailed: number;
  skipped: number;
  saving: number;
  saved: number;
  saveFailed: number;
  deleted: number;
}

export interface BulkListingDraft {
  id: string;
  sessionId: string;
  position: number;
  status: BulkListingDraftStatus;
  imageUrl: string;
  generatedTitle?: string | null;
  generatedDescription?: string | null;
  editedTitle?: string | null;
  editedDescription?: string | null;
  attemptCount: number;
  lastError?: string | null;
  listingId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BulkListingSession {
  id: string;
  userId: string;
  status: BulkListingSessionStatus;
  type: ListingType;
  subtype: ListingSubtype;
  categoryPrimaryId?: string | null;
  categorySecondaryId?: string | null;
  serviceCategoryId?: string | null;
  addressIds: string[];
  groupIds: string[];
  source?: string | null;
  drafts: BulkListingDraft[];
  progress: BulkListingProgress;
  savedListingIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateBulkListingSessionDTO {
  addressIds: string[];
  groupIds: string[];
  type: ListingType;
  subtype?: ListingSubtype;
  categoryPrimaryId?: string | null;
  categorySecondaryId?: string | null;
  serviceCategoryId?: string | null;
  source?: NewListingSource;
}

export interface UpdateBulkListingDraftDTO {
  title?: string;
  description?: string;
  status?: BulkListingDraftStatus;
}

export interface SaveBulkListingDraftDTO {
  title?: string;
  description?: string;
  categoryPrimaryId?: string;
  categorySecondaryId?: string;
  serviceCategoryId?: string;
  addressIds?: string[];
  groupIds?: string[];
  quantity?: number;
  estimatedPrice?: number;
  loanFee?: number;
  loanFeeDescription?: string;
  loanFeePaymentMethod?: string;
  loanFeeDocument?: string | null;
  customDistance?: number;
}

interface BulkListingSessionIdPayload {
  sessionId: string;
}

interface BulkListingDraftPayload extends BulkListingSessionIdPayload {
  draftId: string;
}

export function isBulkListingGenerationActive(session?: BulkListingSession | null) {
  if (!session) return false;
  return session.status === 'generating' || session.progress.queued > 0 || session.progress.generating > 0;
}

export function getBulkDraftTitle(draft: BulkListingDraft) {
  return draft.editedTitle ?? draft.generatedTitle ?? '';
}

export function getBulkDraftDescription(draft: BulkListingDraft) {
  return draft.editedDescription ?? draft.generatedDescription ?? '';
}

export function useBulkListingSession(
  sessionId?: string | null,
  opts: FunctionalUseQueryOptions<BulkListingSession> = {},
) {
  return useQuery<BulkListingSession>({
    queryKey: listingKeys.bulkSession(sessionId ?? 'none'),
    queryFn: () => http.get<BulkListingSession>(`/listings/bulk-sessions/${sessionId!}`).then((res) => res.data),
    ...opts,
    enabled: (opts.enabled ?? true) && Boolean(sessionId),
  });
}

export function useBulkGenerationPolling(
  sessionId?: string | null,
  opts: FunctionalUseQueryOptions<BulkListingSession> = {},
) {
  return useBulkListingSession(sessionId, {
    refetchInterval: (query) => (isBulkListingGenerationActive(query.state.data) ? 1500 : false),
    ...opts,
  });
}

export function useCreateBulkListingSession(
  opts: UseMutationOptions<BulkListingSession, Error, CreateBulkListingSessionDTO> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBulkListingSessionDTO) =>
      http.post<BulkListingSession>('/listings/bulk-sessions', payload).then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUploadBulkListingPhotos(
  opts: UseMutationOptions<
    BulkListingSession,
    Error,
    BulkListingSessionIdPayload & { photos: ImagePickerAsset[] }
  > = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, photos }) =>
      http
        .post<BulkListingSession>(`/listings/bulk-sessions/${sessionId}/photos`, asFormData({ photos }))
        .then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useGenerateBulkListingDrafts(
  opts: UseMutationOptions<BulkListingSession, Error, BulkListingSessionIdPayload & { draftIds?: string[] }> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, draftIds }) =>
      http
        .post<BulkListingSession>(`/listings/bulk-sessions/${sessionId}/generate`, { draftIds })
        .then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUpdateBulkListingDraft(
  opts: UseMutationOptions<BulkListingSession, Error, BulkListingDraftPayload & UpdateBulkListingDraftDTO> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, draftId, ...payload }) =>
      http
        .patch<BulkListingSession>(`/listings/bulk-sessions/${sessionId}/drafts/${draftId}`, payload)
        .then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useRetryBulkListingDraft(
  opts: UseMutationOptions<BulkListingSession, Error, BulkListingDraftPayload> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, draftId }) =>
      http
        .post<BulkListingSession>(`/listings/bulk-sessions/${sessionId}/drafts/${draftId}/retry`)
        .then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useRegenerateBulkListingDraft(
  opts: UseMutationOptions<BulkListingSession, Error, BulkListingDraftPayload> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, draftId }) =>
      http
        .post<BulkListingSession>(`/listings/bulk-sessions/${sessionId}/drafts/${draftId}/regenerate`)
        .then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useSaveBulkListingDraft(
  opts: UseMutationOptions<BulkListingSession, Error, BulkListingDraftPayload & SaveBulkListingDraftDTO> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);
  return useMutation({
    mutationFn: ({ sessionId, draftId, ...payload }) =>
      http
        .post<BulkListingSession>(`/listings/bulk-sessions/${sessionId}/drafts/${draftId}/save`, payload)
        .then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      setSelfUserLoaded(false);
      await Promise.all([qk.selfUser.invalidate(), qk.allListingLists.invalidate()]);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useDeleteBulkListingDraft(
  opts: UseMutationOptions<BulkListingSession, Error, BulkListingDraftPayload> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, draftId }) =>
      http.delete<BulkListingSession>(`/listings/bulk-sessions/${sessionId}/drafts/${draftId}`).then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useReorderBulkListingDrafts(
  opts: UseMutationOptions<BulkListingSession, Error, BulkListingSessionIdPayload & { draftIds: string[] }> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, draftIds }) =>
      http
        .patch<BulkListingSession>(`/listings/bulk-sessions/${sessionId}/drafts/order`, { draftIds })
        .then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useCancelBulkListingSession(
  opts: UseMutationOptions<BulkListingSession, Error, BulkListingSessionIdPayload> = {},
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId }) =>
      http.post<BulkListingSession>(`/listings/bulk-sessions/${sessionId}/cancel`).then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(listingKeys.bulkSession(result.id), result);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUndismissListing(opts: UseMutationOptions<{ success: true }, Error, Listing['id']> = {}) {
  const client = useQueryClient();
  return useMutation<{ success: true }, Error, Listing['id']>({
    mutationFn: (listingId) =>
      http.delete<{ success: true }>(`/listings/dismissals/${listingId}`).then((res) => res.data),
    ...opts,
    onMutate: (listingId) => {
      opts.onMutate?.(listingId);
    },
    onError: (error, listingId, context) => {
      return opts.onError?.(error, listingId, context);
    },
    onSuccess: async (data, variables, context) => {
      // Undo needs active listing queries to refetch immediately because the listing may need
      // to be reinserted into several filtered caches, which we cannot reconstruct reliably here.
      await Promise.all([
        client.invalidateQueries({ queryKey: listingKeys.lists() }),
        client.invalidateQueries({ queryKey: recommendationKeys.lists() }),
      ]);
      return opts.onSuccess?.(data, variables, context);
    },
  });
}

export function useUpdateBulkListingsVisibility() {
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation({
    mutationFn: async ({
      listingIds,
      ...visibility
    }: {
      listingIds: string[];
      addressIds: string[];
      groupIds: string[];
    }) => {
      // Replacement updates are safe to retry if an earlier request succeeded.
      for (const id of new Set(listingIds)) {
        const { data } = await http.patch<Listing>(`/listings/${id}`, visibility);
        client.setQueryData(listingKeys.detail(id), (previous) =>
          hasListingUser(previous) ? mergeListingDetails(previous, data) : data,
        );
      }
    },
    onSettled: () => qk.allListingLists.invalidate(),
  });
}
